/**
 * Leitura dos botões de um template HSM (component BUTTONS) pro modal de
 * "iniciar atendimento": desenhar na prévia e coletar o link de botões de URL
 * dinâmica.
 *
 * ⚠️ O `index` do botão é a POSIÇÃO no array `buttons` (incluindo QUICK_REPLY e
 * PHONE) — o MESMO número que o servidor usa em `buildTemplateComponents` como
 * `index` do component e como chave `btn:<index>` da variável. Se as duas
 * contagens divergirem, o valor entra no botão errado (ou some) e a Meta recusa
 * com `131008`. Por isso a iteração aqui é sobre o array CRU, sem filtrar antes.
 */

interface ComponenteBruto {
  type?: string;
  buttons?: Array<{ type?: string; text?: string; url?: string }>;
}

export interface BotaoTemplate {
  /** Posição no array de botões (0-based) — bate com o `index` que vai pra Meta. */
  index: number;
  /** 'url' | 'quick_reply' | 'phone_number' | ... (minúsculo). */
  type: string;
  text: string;
  url?: string;
}

function blocoDeBotoes(components: unknown): ComponenteBruto['buttons'] | null {
  if (!Array.isArray(components)) return null;
  const bloco = (components as ComponenteBruto[]).find(
    (c) => (c.type ?? '').toUpperCase() === 'BUTTONS',
  );
  return bloco?.buttons ?? null;
}

/** Botões do template, na ordem em que a Meta os numera (pra prévia). */
export function botoesDoTemplate(components: unknown): BotaoTemplate[] {
  const buttons = blocoDeBotoes(components);
  if (!buttons) return [];
  return buttons
    .map((b, index) => ({
      index,
      type: (b.type ?? '').toLowerCase(),
      text: b.text ?? '',
      ...(b.url ? { url: b.url } : {}),
    }))
    .filter((b) => b.text);
}

export interface BotaoDinamico {
  index: number;
  text: string;
  /** Template da URL, ainda com `{{N}}`. */
  url: string;
}

/**
 * Botões de URL com variável (`https://.../{{N}}`). Cada um exige um valor no
 * envio (a Meta não preenche URL dinâmica sozinha). O `index` é a chave da
 * variável em `btn:<index>`.
 */
export function botoesDinamicosDeUrl(components: unknown): BotaoDinamico[] {
  const buttons = blocoDeBotoes(components);
  if (!buttons) return [];
  const out: BotaoDinamico[] = [];
  buttons.forEach((b, index) => {
    if ((b.type ?? '').toUpperCase() !== 'URL' || !b.url) return;
    if (!/\{\{\d+\}\}/.test(b.url)) return; // URL fixa → Meta preenche
    out.push({ index, text: b.text ?? '', url: b.url });
  });
  return out;
}

/** O prefixo fixo da URL (sem o `{{N}}`) — dica visual do que o atendente completa. */
export function prefixoDaUrl(url: string): string {
  return url.replace(/\{\{\d+\}\}.*$/, '');
}
